import {
  Component,
  computed,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { EnvToken, SeoSegmentsToken } from '@app/core/env.tokens';
import { TipsService, TipsIndex } from '@services/http/tips.service';
import { FooterComponent } from '@components/shared/footer/footer.component';
import { ActivatedRoute, ParamMap, RouterLink } from '@angular/router';
import { Tip } from '@models/guides/tips.model';
import { DecimalPipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { BreadcrumbListComponent } from '@components/shared/breadcrumb-list/breadcrumb-list.component';
import {
  catchError,
  distinctUntilChanged,
  map,
  of,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { SEOService } from '@services/logic/seo.service';
import { TranslatePipe } from '@app/pipes/translate.pipe';
import { flattenTipGroups, tipPath } from './tips-index.helpers';
import { indexPagePath } from '@helpers/indexPagePath';

interface CategoryRoute {
  mainCategory: string;
  subCategory: string;
  subSubCategory: string;
}

@Component({
  selector: 'app-tips-index',
  imports: [
    FooterComponent,
    MatIcon,
    BreadcrumbListComponent,
    DecimalPipe,
    RouterLink,
    TranslatePipe,
  ],
  providers: [TranslatePipe],
  templateUrl: './tips-index.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './tips-index.component.scss',
})
export class TipsIndexComponent {
  private readonly env = inject(EnvToken);
  private readonly seoSegments = inject(SeoSegmentsToken);
  private readonly tipsService = inject(TipsService);
  private readonly translatePipe = inject(TranslatePipe);
  private readonly seoService = inject(SEOService);
  private readonly activatedRoute = inject(ActivatedRoute);

  private readonly route = toSignal(
    this.activatedRoute.paramMap.pipe(map(toCategoryRoute)),
    { initialValue: toCategoryRoute(this.activatedRoute.snapshot.paramMap) },
  );

  /** `undefined` while loading. */
  private readonly response = toSignal(
    this.activatedRoute.paramMap.pipe(
      map(toCategoryRoute),
      distinctUntilChanged(
        (previous, current) =>
          JSON.stringify(previous) === JSON.stringify(current),
      ),
      tap(() => this.seoService.setCanonicalUrl()),
      switchMap((route) => {
        const slug = activeCategory(route);
        if (!slug) {
          return of({ tips: [] } as Partial<TipsIndex>);
        }
        return this.fetchIndex(slug).pipe(
          tap((index) => {
            if (index.indexPath) {
              this.seoService.setCanonicalUrl(
                `${this.env.domain}${index.indexPath}`,
              );
            }
          }),
          startWith(undefined),
        );
      }),
    ),
    { initialValue: undefined },
  );

  protected readonly loading = computed(() => this.response() === undefined);
  protected readonly index = computed(() => {
    const response = this.response();
    return response?.indexPath ? (response as TipsIndex) : null;
  });
  protected readonly tips = computed(() => this.response()?.tips ?? []);
  protected readonly subCategories = computed(
    () => this.response()?.subCategories ?? [],
  );

  protected readonly activeCategoryTitle = computed(
    () =>
      this.index()?.category?.title ||
      toReadableText(activeCategory(this.route())),
  );

  protected readonly parentIndexPath = computed(() => {
    const index = this.index();
    if (!index?.parentCategory) {
      return '';
    }
    return index.indexPath.replace(
      `/${index.category.slug}/${this.seoSegments.indexPage.tips}`,
      `/${this.seoSegments.indexPage.tips}`,
    );
  });

  protected readonly partsOfUrl = computed(() => {
    const { mainCategory, subCategory, subSubCategory } = this.route();
    const hubIndex = this.seoSegments.suffix.hubIndex;
    const parts: { name: string; url: string }[] = [];
    if (mainCategory) {
      parts.push({
        name: toReadableText(mainCategory),
        url: `${this.env.domain}/${mainCategory}/${hubIndex}`,
      });
    }
    if (mainCategory && subCategory && !subSubCategory) {
      parts.push({
        name: toReadableText(subCategory),
        url: `${this.env.domain}/${mainCategory}/${subCategory}/${hubIndex}`,
      });
    } else if (mainCategory && subCategory && subSubCategory) {
      parts.push({
        name: toReadableText(subSubCategory),
        url: `${this.env.domain}/${mainCategory}/${subCategory}/${subSubCategory}/${hubIndex}`,
      });
    }
    if (activeCategory(this.route())) {
      parts.push({
        name: this.translatePipe.transform('SEO.SCHEMA.TIPS_INDEX_NAME'),
        url: `${this.env.domain}${indexPagePath(
          this.seoSegments.indexPage.tips,
          [mainCategory, subCategory, subSubCategory],
        )}`,
      });
    }
    return parts;
  });

  protected linkFor(tip: Tip): string {
    const { mainCategory, subCategory } = this.route();
    return tipPath(tip, this.seoSegments, [mainCategory, subCategory]);
  }

  private fetchIndex(category: string) {
    return this.tipsService.fetchTipsIndexByCategorySlug(category).pipe(
      map((index): Partial<TipsIndex> => index),
      // TODO: remove once the dashboard index endpoint is deployed.
      catchError(() =>
        this.tipsService.fetchTipsByAsCat(category).pipe(
          map(
            (groups): Partial<TipsIndex> => ({
              tips: flattenTipGroups(groups),
            }),
          ),
          catchError(() => of<Partial<TipsIndex>>({ tips: [] })),
        ),
      ),
    );
  }
}

function toCategoryRoute(params: ParamMap): CategoryRoute {
  return {
    mainCategory: params.get('headCategory') ?? '',
    subCategory: params.get('subCategory') ?? '',
    subSubCategory: params.get('subSubCategory') ?? '',
  };
}

function activeCategory({
  mainCategory,
  subCategory,
  subSubCategory,
}: CategoryRoute): string {
  return subSubCategory || subCategory || mainCategory;
}

function toReadableText(category: string): string {
  return category
    .replace(/-/g, ' ')
    .replace(/oe/g, 'ø')
    .replace(/aa/g, 'å')
    .replace(/ae/g, 'æ');
}
